import * as Sentry from '@sentry/react';
import { TECHNOLOGIES_BY_GROUP } from 'constants/products';
import { fileTypeFromBlob } from 'file-type';
import { Technology } from 'utils/products';
import {
  FileValidationResult,
  IPreview,
  ValidateFilesProps,
} from '../dropzone.types';
import {
  IMAGE_MAX_FILE_SIZE_BYTES,
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  VIDEO_MAX_FILE_SIZE_BYTES,
} from '../dropzone.yup';
import { getFileExtension, isImage, isPreviewableVideo, isVideo } from './file';
import {
  formatDuplicateFileErrorMessage,
  formatFileCountErrorMessage,
  formatFileExtensionsErrorMessage,
  formatFileExtensionsMatchTypeErrorMessage,
  formatFileSizeErrorMessage,
  formatImageResolutionErrorMessage,
} from './messages';

export const validateDuplicateFilenames = (
  newFiles: File[],
  existingFilenames: Set<string>,
): boolean => {
  return newFiles.every((file) => !existingFilenames.has(file.name));
};

export const validateFileCount = (
  newFilesCount: number,
  currentCount: number,
  maxFiles: number,
): boolean => {
  return currentCount + newFilesCount <= maxFiles;
};

export const validateFileExtensions = (
  files: File[],
  acceptedExtensions: string,
): boolean => {
  const allowed = acceptedExtensions
    .split(',')
    .map((ext) => ext.trim().toLowerCase())
    .filter(Boolean);

  return files.every((file) => {
    const ext = getFileExtension(file);
    return allowed.includes(ext);
  });
};

// File types that we know we can't detect (.mp4 sometimes fails to be detected)
const knownUndetectedFileTypes = ['.mp4', '.gvf'];

export const validateFileExtensionsMatchType = async (
  files: File[],
  acceptedExtensions: string,
): Promise<boolean> => {
  const allowed = acceptedExtensions
    .split(',')
    .map((ext) => ext.trim().toLowerCase())
    .filter(Boolean);

  const results = await Promise.all(
    files.map(async (file) => {
      const type = await fileTypeFromBlob(file);

      const fileExtension = getFileExtension(file);

      // Exclude file types that we know we can't detect
      if (!type && !knownUndetectedFileTypes.includes(fileExtension)) {
        Sentry.captureMessage(
          `Undetected file type uploaded: ${fileExtension}`,
          'warning',
        );
      }

      // Could not detect type - this can happen, for example, with .gvf and some .mp4 files
      if (!type) return true;

      // .wmv files are intentionally detected as .asf
      // https://github.com/sindresorhus/file-type/pull/398
      if (type.ext === 'asf' && allowed.includes('.wmv')) {
        return true;
      }

      return allowed.includes(`.${type.ext}`);
    }),
  );

  return results.every(Boolean);
};

export const validateFileSize = (
  files: File[],
): { oversizedImages: File[]; oversizedVideos: File[] } => {
  const oversizedImages: File[] = [];
  const oversizedVideos: File[] = [];

  for (const file of files) {
    const ext = getFileExtension(file);

    if (isImage(ext) && file.size > IMAGE_MAX_FILE_SIZE_BYTES) {
      oversizedImages.push(file);
    } else if (isVideo(ext) && file.size > VIDEO_MAX_FILE_SIZE_BYTES) {
      oversizedVideos.push(file);
    }
  }

  return { oversizedImages, oversizedVideos };
};

export const validateResolutionAndBuildPreviews = async (
  files: File[],
): Promise<{ previews: IPreview[]; resolutionError: boolean }> => {
  const previews: IPreview[] = files.map((file) => ({
    name: file.name,
    type: isImage(getFileExtension(file))
      ? 'image'
      : isPreviewableVideo(getFileExtension(file))
        ? 'video'
        : 'unknown',
    url: URL.createObjectURL(file),
  }));

  const imageFilesWithUrls = previews.filter(({ type }) => type === 'image');

  if (imageFilesWithUrls.length === 0) {
    return { previews, resolutionError: false };
  }

  const resolutionChecks = await Promise.all(
    imageFilesWithUrls.map(
      ({ url }) =>
        new Promise<{ valid: boolean; url: string }>((resolve) => {
          const img = new Image();
          img.onload = () => {
            const valid =
              img.width <= IMAGE_MAX_WIDTH && img.height <= IMAGE_MAX_HEIGHT;
            resolve({ valid, url });
          };
          img.onerror = () => resolve({ valid: false, url });
          img.src = url;
        }),
    ),
  );

  const hasInvalidResolution = resolutionChecks.some((result) => !result.valid);

  if (hasInvalidResolution) {
    previews.forEach(({ url }) => URL.revokeObjectURL(url));
    return { previews: [], resolutionError: true };
  }

  return { previews, resolutionError: false };
};

export const validateFiles = async ({
  files = [],
  newFiles,
  maxFiles,
  acceptedExtensions,
  rejectedFiles,
}: ValidateFilesProps): Promise<FileValidationResult> => {
  const errors: string[] = [];
  const existingFilenames = new Set(files.map((file) => file.name));
  const droppedFilesCounter = newFiles.length + (rejectedFiles?.length || 0);

  // # Duplicate filenames
  if (!validateDuplicateFilenames(newFiles, existingFilenames)) {
    errors.push(formatDuplicateFileErrorMessage(acceptedExtensions));
  }

  // # File count
  if (!validateFileCount(newFiles.length, files.length, maxFiles)) {
    errors.push(formatFileCountErrorMessage(maxFiles, acceptedExtensions));
  }

  // # File extension
  const invalidType = rejectedFiles?.some((file) =>
    file.errors.some((err) => err.code === 'file-invalid-type'),
  );

  if (invalidType || !validateFileExtensions(newFiles, acceptedExtensions)) {
    errors.push(
      formatFileExtensionsErrorMessage(droppedFilesCounter, acceptedExtensions),
    );
  }

  // Due to limitations in the verification of video files, we are not doing this check for Deep Capsule.
  // https://digestaid.atlassian.net/browse/WEB-1154?focusedCommentId=23381
  if (Technology.isAvailable(TECHNOLOGIES_BY_GROUP.detection)) {
    // # File extension matches file type
    const extensionsMatchType = await validateFileExtensionsMatchType(
      newFiles,
      acceptedExtensions,
    );

    if (!extensionsMatchType) {
      errors.push(
        formatFileExtensionsMatchTypeErrorMessage(
          droppedFilesCounter,
          acceptedExtensions,
        ),
      );
    }
  }

  // # File size
  const { oversizedImages, oversizedVideos } = validateFileSize(newFiles);
  if (oversizedImages.length > 0 || oversizedVideos.length > 0) {
    errors.push(
      formatFileSizeErrorMessage(
        droppedFilesCounter,
        oversizedImages.length > 0,
        oversizedVideos.length > 0,
      ),
    );
  }

  if (errors.length > 0) {
    return { previews: [], errors };
  }

  // # Build object URLs and validate image resolution
  // # Resolution check is heavy, so we do it last if no other errors are found
  const { previews, resolutionError } =
    await validateResolutionAndBuildPreviews(newFiles);

  if (resolutionError) {
    errors.push(formatImageResolutionErrorMessage(newFiles.length));
    return { previews: [], errors };
  }

  return { previews, errors: [] };
};
